import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Progress } from '@/components/ui/progress-circle';
import { TenantPolicy, Usage } from '@activepieces/shared';

type TasksUsageProps = {
  usage: Usage;
  policy: TenantPolicy;
};

const getUsageProgressColor = (percentage: number): string | undefined => {
  if (percentage >= 100) return 'bg-destructive';
  if (percentage >= 80) return 'bg-warning';
  return undefined;
};

export const TasksUsage = ({ usage, policy }: TasksUsageProps) => {
  const { t, i18n } = useTranslation();
  const percentage = Math.min(
    100,
    (usage.tasks / policy.monthlyTasksQuota) * 100,
  );

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between gap-1 text-xs">
        <span className="font-medium uppercase text-muted-foreground">
          {t('Executions this month')}
        </span>
        <div className="flex items-center gap-[2px] whitespace-nowrap">
          <span className="text-sm font-semibold">
            {usage.tasks.toLocaleString()}
          </span>
          <span className="text-muted-foreground/80">/</span>
          <span className="text-muted-foreground/80">
            {policy.monthlyTasksQuota.toLocaleString()}
          </span>
        </div>
      </div>
      <Progress
        value={percentage}
        className="h-1"
        indicatorClassName={getUsageProgressColor(percentage)}
      />
      {percentage >= 100 && (
        <div className="flex items-center gap-2 text-xs text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>
            {t('Executions limit reached — flows blocked until {date}', {
              date: new Date(usage.resetDate).toLocaleDateString(
                i18n.language,
                {
                  day: 'numeric',
                  month: 'long',
                },
              ),
            })}
          </span>
        </div>
      )}
    </div>
  );
};
